import { Utils, controllerFor } from './utils';

/**
 * Base options for component initialization.
 */
export interface BaseOptions {}

export type InitElement = HTMLElement | Element;
export type InitElements<T extends InitElement> = NodeListOf<T> | HTMLCollectionOf<T>;
/** Any component class; `init` reads its element, options and instance types. */
type ComponentClass = (new (el: never, options: never) => Component<BaseOptions>) & { key: string };
/** The element a component class is constructed on. */
type ElementOf<C extends ComponentClass> = C extends new (el: infer E, options: never) => unknown ? E : never;
/** The options `init` accepts for a component class. */
type InitOptions<C extends ComponentClass> = Partial<InstanceType<C>['options']>;

export interface I18nOptions {
  cancel: string;
  clear: string;
  done: string;
}

/** What {@link Component._notify} passes on, and when it stops. */
export interface NotifyOptions {
  args?: unknown[];
  detail?: unknown;
  current?: () => boolean;
}

/**
 * Base class implementation for Expressive components.
 */
export class Component<O extends BaseOptions> {
  /**
   * The element property that holds an instance, `Expressive_<Name>`, so page
   * code can read `el.Expressive_Tabs` as well as call `Tabs.getInstance(el)`.
   */
  static key: string;
  /**
   * The DOM element the plugin was initialized with.
   */
  el: HTMLElement;
  /**
   * The options the instance was initialized with.
   */
  options: O;
  /** Aborted by destroy(), which removes every listener added with its signal. */
  protected _events: AbortController;

  /**
   * Destroys any instance already on `el`, then spreads `options` over the
   * class defaults, and `options.i18n` over the default strings, so passing
   * one string keeps the rest. A subclass passes options it reads from
   * markup in `options`.
   */
  constructor(el: HTMLElement, options: Partial<O>) {
    // Display error if el is not a valid HTML Element
    if (!(el instanceof HTMLElement)) {
      console.error(Error(el + ' is not an HTML Element'));
    }
    new.target.getInstance(el)?.destroy();
    this.el = el;
    const defaults: Partial<O> & { i18n?: object } = new.target.defaults;
    const i18n: object = defaults.i18n && { i18n: { ...defaults.i18n, ...(options as typeof defaults)?.i18n } };
    this.options = { ...defaults, ...options, ...i18n } as O;
    this._events = controllerFor(el);
    el[new.target.key] = this;
  }

  /**
   * Initializes an instance of the class it is called on.
   * @param el HTML element.
   * @param options Component options.
   */
  static init<C extends ComponentClass>(this: C, el: ElementOf<C>, options?: InitOptions<C>): InstanceType<C>;
  /**
   * Initializes an instance of the class it is called on for each element.
   * @param els HTML elements.
   * @param options Component options.
   */
  static init<C extends ComponentClass>(
    this: C,
    els: InitElements<InitElement>,
    options?: InitOptions<C>
  ): InstanceType<C>[];
  static init<C extends ComponentClass>(
    this: C,
    els: ElementOf<C> | InitElements<InitElement>,
    options: InitOptions<C> = {}
  ): InstanceType<C> | InstanceType<C>[] {
    const create = (el: unknown) => new this(el as never, options as never) as InstanceType<C>;
    if (els instanceof Element) return create(els);
    return els ? Array.from(els as InitElements<InitElement>, create) : null;
  }

  /**
   * @returns default options for component instance.
   */
  static get defaults(): BaseOptions {
    return {};
  }

  /**
   * Retrieves the instance of the class it is called on for the given element.
   * @param el Associated HTML Element.
   */
  static getInstance<C extends ComponentClass>(this: C, el: HTMLElement): InstanceType<C> {
    return el[this.key];
  }

  /**
   * Runs an `on…` option callback, then fires `expressive:<type>` from the
   * element, so pages can listen instead of passing options. The callback gets
   * `args` (the element by default) and the event carries `detail`.
   *
   * A callback or listener may close, reopen or destroy the component. Pass
   * `current` to tell whether the state `type` names still holds; the event
   * is skipped once it does not, or once the instance is destroyed, and the
   * result says whether to carry on.
   */
  protected _notify(
    type: string,
    callback?: Function | null,
    { args = [this.el], detail, current = () => true }: NotifyOptions = {}
  ): boolean {
    const live = () => (this.constructor as typeof Component).getInstance(this.el) === this && current();
    if (typeof callback === 'function') callback.apply(this, args);
    if (!live()) return false;
    Utils.emit(this.el, type, detail);
    return live();
  }

  /**
   * Destroy plugin instance and teardown. A subclass tears down its own state
   * and calls this to remove its listeners and release the element.
   */
  destroy(): void {
    this._events.abort();
    this.el[(this.constructor as typeof Component).key] = undefined;
  }
}
